/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { BackIcon, DownloadIcon } from '../../../../assets/icons';
import { Constants, formatMiles, fromStorage, getUpdatedState } from '../../../../utils';
import { FallbackImage, Pagination } from '../../../../components';
import { ReportService } from '../../../../services';
import { Photo } from '../../../../assets/img';

const VehicleFailures = () => {
  const { data, downloadAll, goToPage, pagination, vehicle } = useFetchTable();

  return (
    <main className="p-4 w-full">
      <div className="flex items-center gap-4 mb-4">
        <Link to="/report/failures">
          <img src={BackIcon} className="w-6 h-6 object-contain cursor-pointer invert" alt="" />
        </Link>

        <h1 className="text-3xl font-bold">{ vehicle?.plaque || '--' }</h1>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="flex items-center justify-between py-4 px-3">
          <p className="text-2xl font-bold text-center">Historial del vehículo</p>

          <button
            className="flex items-center gap-2 bg-black text-white px-4 py-1.5 rounded-lg font-semibold"
            onClick={() => toast.promise(downloadAll(), { loading: 'Descargando...', success: 'Reporte descargado con éxito', error: 'Error al descargar el reporte' })}
          >
            <img src={DownloadIcon} className="w-4 h-4 brightness-200" alt="" />
            Descargar
          </button>
        </div>

        <div className="overflow-x-auto px-4">
          <div className="space-y-4">
            {data.map((item) => (
              <div 
                key={item.id} 
                className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 items-center py-2 bg-white border border-gray6 rounded-xl hover:shadow-md transition-shadow text-sm"
              >
                <div>
                  <FallbackImage
                    src={item.evidence}
                    placeholder={Photo}
                    className="w-20 h-20 rounded-lg ml-2"
                    placeholderClassName="w-20 h-20 rounded-lg ml-2"
                  />
                </div>
                <div>
                  <p className="font-semibold">Fecha de falla o mantenimiento</p>
                  <p className="text-base text-text">{ moment(item.created_at).format('DD/MM/YYYY') }</p>
                </div>
                <div>
                  <p className="font-semibold">Conductor</p>
                  <p className="text-base text-text">{ item.driver?.person?.name }</p>
                </div>
                <div>
                  <p className="font-semibold">Categoría de servicio</p>
                  <p className="text-base text-text">{ item.category?.name }</p>
                </div>
                <div>
                  <p className="font-semibold">Motivo de servicio</p>
                  <p className="text-base text-text">{ item.reason }</p>
                </div>
                <div>
                  <p className="font-semibold">Kilometraje</p>
                  <p className="text-base text-text">{ formatMiles(item.mileage, false, '') }</p>
                </div>
                <div>
                  <p className="font-semibold">Estatus</p>
                  <p
                    className="text-base font-semibold text-default data-[status=0]:text-red data-[status=1]:text-green"
                    data-status={item.status}
                  >
                    { item.status_text }
                  </p>
                </div>
              </div>
            ))}
          </div>

          {!data.length && (
            <p className="py-8 text-text text-center">Sin datos para mostrar</p>
          )}
        </div>

        <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Pagination
            active={pagination.page}
            pages={pagination.pages}
            onChange={goToPage}
          />
        </div>
      </div>
    </main>
  );
};

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    type: '',
    status: '',
    plaque: ''
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const params = useParams();
  const [data, setData] = useState([]);
  const [vehicle, setVehicle] = useState();
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const payload = await getUpdatedState(setFilters);
      payload.plaque = params.plaque;
      const response = await ReportService.owner.failures.findOne(payload);
      const { data, vehicle, ...rest } = response;

      setData(data);
      setVehicle(vehicle);
      setPagination(rest);

    } catch (error) {
      setData([]);
      toast.error(String(error));

    } finally {
      setCanFetch(true);
    }
  }

  const downloadAll = async () => {
    try {
      const payload = await getUpdatedState(setFilters);
      payload.plaque = params.plaque;
      payload.download = true;
      const response = await ReportService.owner.failures.findOne(payload);
      if (response.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      toast.error(String(error));
    }
  }

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    fetchData();
  }

  const goToPage = async (page) => {
    const pagination = await getUpdatedState(setPagination);
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
      fetchData();
    }
  }

  const onChange = (target, value) => {
    setFilters(s => ({ ...s, page: 1, [target]: value }));
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    downloadAll,
    filters,
    goToPage,
    isLoading: !canFetch,
    onChange,
    pagination,
    reload,
    search: fetchData,
    vehicle,
  }
}

export default VehicleFailures;